import { ChevronRightIcon } from '@/components/icons/ChevronRightIcon';
import {
  GUT_DYBIOSIS_PATH,
  GUT_RICHNESS_PATH,
  ORAL_DYBIOSIS_PATH,
  ORAL_RICHNESS_PATH,
} from '@/constants/paths';
import { EventTracker } from '@/utils';
import { Button } from '@design-system/Button';
import { GutOralCardsLg } from '@design-system/HealthSections/GutOralCards';
import { Link } from '@design-system/Link';
import { NavBar } from '@design-system/NavBar';
import { Skeleton } from '@design-system/Skeleton';
import { StandardContainer } from '@design-system/StandardContainer';
import { NutritionJourneyDeeperSection } from '@screens/NutritionPage/NutritionJourneyDeeperSection';
import { JourneyEducationTypes } from '@utils/JourneyEducationTypes';
import {
  getDysbiosisAnimation,
  getFallBackVdoDysbiosisAnimation,
  getFallBackVdoRichnessAnimation,
  getRichnessAnimation,
} from '@utils/health/getAnimation';
import { MicrobiomeDetailResponse } from '@viome/viome-base/src/usecase/Health/Health.model';
import { useNavigate } from 'react-router-dom';

interface ReuseableHealthViewProps {
  isLoading: boolean;
  profileData: MicrobiomeDetailResponse | null;
  buttonText: string;
  onClickPath: string;
  type: 'oral' | 'gut';
}

const handleOnClickPath = (onClickPath: string, type: string) => {
  EventTracker.trackEvent(
    'health',
    `health_${type == 'oral' ? 'myOral' : 'myGut'}Microbiome_microbes`,
  );
  return onClickPath;
};

export const HealthView: React.FC<ReuseableHealthViewProps> = ({
  isLoading,
  profileData,
  buttonText,
  onClickPath,
  type = 'gut',
}) => {
  const navigate = useNavigate();
  const {
    title = '',
    about = '',
    richness = null,
    dysbiosis = null,
  } = profileData || {};

  const navigateToRichness = () => {
    EventTracker.trackEvent(
      'health',
      `health_${type == 'oral' ? 'myOral' : 'myGut'}Microbiome_richnessDiversity`,
    );
    navigate(`${type == 'oral' ? ORAL_RICHNESS_PATH : GUT_RICHNESS_PATH}`);
  };

  const navigateToDysbiosis = () => {
    EventTracker.trackEvent(
      'health',
      `health_${type == 'oral' ? 'myOral' : 'myGut'}Microbiome_dysbiosis`,
    );
    navigate(`${type == 'oral' ? ORAL_DYBIOSIS_PATH : GUT_DYBIOSIS_PATH}`);
  };

  return (
    <div className="dark bg-black h-full w-full">
      <StandardContainer>
        <NavBar returnButtonTitle="Health" className="md:-ml-5 mt-12" />

        <div className="mt-12">
          {isLoading ? (
            <div className="mb-6">
              <Skeleton className="h-10 w-80 mb-6" />
              <Skeleton className="w-full h-4 max-w-[37rem] mb-2" />
              <Skeleton className="w-full h-4 max-w-[37rem]" />
            </div>
          ) : (
            <>
              <h1 className="dark lg:typography-d1 typography-d2 text-primary mb-4">
                {title || ''}
              </h1>
              <p className="typography-caption1 sm:typography-body dark text-secondary max-w-[37rem] mb-12">
                {about || ''}
              </p>
            </>
          )}

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-2 place-items-center">
              <Skeleton className="h-[24rem] w-full border border-stroke-static my-4 rounded-lg" />
              <Skeleton className="h-[24rem] w-full border border-stroke-static my-4 rounded-lg" />
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 gap-2 place-items-center">
                {richness && (
                  <GutOralCardsLg
                    title={richness.title}
                    isMainTextPercentage
                    mainText={`${richness.percentage}`}
                    subText={richness.rank}
                    subTextColor={richness.rank_color}
                    videoSrc={getRichnessAnimation(type)}
                    fallBackVdo={getFallBackVdoRichnessAnimation(type)}
                    footer={`This score measures the diversity of active microbes in your ${type === 'gut' ? 'gut' : 'mouth'}. `}
                    onClick={navigateToRichness}
                  />
                )}
                {dysbiosis && (
                  <GutOralCardsLg
                    title={dysbiosis.title}
                    mainText={dysbiosis.message}
                    subTextColor={dysbiosis.rank_color}
                    subText={dysbiosis.rank}
                    videoSrc={getDysbiosisAnimation(type)}
                    fallBackVdo={getFallBackVdoDysbiosisAnimation(type)}
                    footer={`This score assesses the level of disruption in the microbial balance and activities of your ${type} microbiome.`}
                    onClick={navigateToDysbiosis}
                  />
                )}
              </div>
              <Button
                asChild
                variant="secondary"
                className="dark w-full h-12 [&_svg]:!size-6 justify-between pr-3 mt-6"
              >
                <Link
                  className="dark"
                  to={handleOnClickPath(onClickPath, type)}
                >
                  {buttonText}
                  <ChevronRightIcon height={24} width={24} />
                </Link>
              </Button>
            </>
          )}
          <div className="mt-6 mb-12">
            <NutritionJourneyDeeperSection
              sectionInfo={
                type === 'oral'
                  ? JourneyEducationTypes.ORAL_MICROBIOME
                  : JourneyEducationTypes.GUT_MICROBIOME
              }
              screen={
                type === 'oral'
                  ? 'health_myOralMicrobiome'
                  : 'health_myGutMicrobiome'
              }
            />
          </div>
        </div>
      </StandardContainer>
    </div>
  );
};
